/**
 * Transcript export for live analytics rows. Demo rows are rendered from the
 * bundled sample text; live rows fetch the real
 * `live_avatar_session_transcript` artifact recorded at session end.
 */
import { powtoonBE } from "@/lib/powtoon-be/client";
import { createLogger } from "@/lib/frontend-logger";
import { ORIGIN_META } from "./demo-data";
import type { LiveSession } from "./live-client";

const logger = createLogger("analytics.live-transcript");

type Turn = {
  role?: string;
  transcript?: string;
  relative_timestamp?: number;
};

function clock(seconds: number | undefined): string {
  const s = Math.max(0, Math.round(seconds ?? 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function save(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadLiveTranscript(session: LiveSession): Promise<void> {
  const header = [
    "Live Avatar session transcript",
    `Learner: ${session.learner}${session.learnerId ? ` [${session.learnerId}]` : " [anonymous]"}`,
    `Date: ${session.when}   Origin: ${ORIGIN_META[session.origin].label}   Duration: ${session.min} min   Completed: ${session.completed ? "Yes" : "No"}`,
    "",
    "--- Transcript ---",
  ];

  if (!session.transcriptObjectId || !session.scenarioId) {
    save(`transcript_${session.id}.txt`, [...header, "(no transcript was recorded for this session)"].join("\n"));
    return;
  }

  try {
    const artifact = await powtoonBE.sessions.getTranscript(
      session.scenarioId,
      session.transcriptObjectId,
    );
    const turns = (artifact.content?.transcript as Turn[] | undefined) ?? [];
    const lines = turns.map(
      (t) =>
        `[${clock(t.relative_timestamp)}] ${t.role === "user" ? session.learner : "Avatar"}: ${t.transcript ?? ""}`,
    );
    save(
      `transcript_${session.id}.txt`,
      [...header, ...(lines.length ? lines : ["(transcript is empty)"])].join("\n"),
    );
  } catch (e) {
    logger.error("live transcript download failed", {
      session_id: session.id,
      error: (e as Error)?.message ?? String(e),
    });
    save(
      `transcript_${session.id}.txt`,
      [...header, "(transcript could not be loaded)"].join("\n"),
    );
  }
}
